<template>
  <div
    ref="scrollEl"
    class="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-track-transparent relative pt-5"
    @scroll="onScroll"
  >
    <!-- Top: load older -->
    <div v-if="hasMore" class="flex justify-center py-2">
      <button
        class="text-[10px] uppercase tracking-widest text-text-muted hover:text-white"
        :disabled="loadingMore"
        @click="onLoadOlder"
      >
        {{ loadingMore ? '…' : $t('chat.actions.load_older') }}
      </button>
    </div>

    <div v-if="loading" class="flex justify-center py-8">
      <div class="w-6 h-6 border-2 border-white/10 border-t-primary rounded-full animate-spin"></div>
    </div>

    <div v-else-if="messages.length === 0" class="flex flex-col items-center justify-center h-full py-12 text-text-dim text-sm">
      {{ emptyLabel }}
    </div>

    <ChatMessageItem
      v-for="msg in messages"
      :key="msg.id || msg.clientMsgId"
      :ref="(el) => registerMessageEl(msg.id, el)"
      :message="msg"
      :can-pin-messages="canPinMessages"
      :class="highlightedId === msg.id ? 'msg-highlight' : ''"
      :data-msg-id="msg.id"
      @retry="(id) => $emit('retry', id)"
      @delete="(id) => $emit('delete', id)"
      @mute="(info) => $emit('mute', info)"
      @reply="(m) => $emit('reply', m)"
      @pin="(id) => $emit('pin', id)"
      @jump-to="(id) => $emit('jump-to', id)"
    />

    <!-- Floating "new messages" pill when scrolled up -->
    <div v-if="newMessagesBelow > 0" class="sticky bottom-2 flex justify-center pointer-events-none">
      <button
        class="pointer-events-auto px-3 py-1 rounded-full bg-primary text-black text-xs font-semibold shadow-lg"
        @click="scrollToBottom(true)"
      >
        ↓ {{ newMessagesBelow }} {{ newMessagesBelow === 1 ? 'new' : 'new' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, onMounted } from 'vue'

const props = defineProps({
  messages: { type: Array, required: true },
  loading: { type: Boolean, default: false },
  loadingMore: { type: Boolean, default: false },
  hasMore: { type: Boolean, default: false },
  emptyLabel: { type: String, default: '' },
  // Forwarded down to each ChatMessageItem so the action kebab shows
  // the Pin option only for users who actually have permission.
  canPinMessages: { type: Boolean, default: false }
})
const emit = defineEmits(['retry', 'load-older', 'delete', 'mute', 'reply', 'pin', 'jump-to'])

// Per-message component refs — populated by the v-for inline ref callback.
// Used by scrollToMessage (pinned-banner click, quote-reply jump-to).
const messageEls = new Map()
function registerMessageEl(id, el) {
  if (!id) return
  if (el) messageEls.set(String(id), el)
  else messageEls.delete(String(id))
}

const highlightedId = ref(null)
async function scrollToMessage(id) {
  if (!id) return
  await nextTick()
  const inst = messageEls.get(String(id))
  const el = inst?.$el
    || (typeof document !== 'undefined'
      ? document.querySelector(`[data-msg-id="${id}"]`)
      : null)
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  highlightedId.value = String(id)
  setTimeout(() => {
    if (highlightedId.value === String(id)) highlightedId.value = null
  }, 1400)
}
defineExpose({ scrollToMessage })

const scrollEl = ref(null)
const newMessagesBelow = ref(0)
const pinnedToBottom = ref(true)
let prevLastId = null
let prevFirstId = null
// Snapshot taken when the user clicks "load older" — used after the prepend
// finishes rendering to keep the previously-visible message in the same spot.
let pendingScrollAnchor = null

function isNearBottom() {
  const el = scrollEl.value
  if (!el) return true
  const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
  return distanceFromBottom < 120
}

function scrollToBottom(smooth = false) {
  const el = scrollEl.value
  if (!el) return
  el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' })
  newMessagesBelow.value = 0
  pinnedToBottom.value = true
}

function onScroll() {
  pinnedToBottom.value = isNearBottom()
  if (pinnedToBottom.value) newMessagesBelow.value = 0
}

function onLoadOlder() {
  // Capture the pre-load scroll metrics so the messages watcher can keep the
  // user's viewport anchored on the same line after older messages are
  // prepended. Without this, scrollTop stays at 0 and the list jumps to the
  // top of the freshly-loaded batch.
  const el = scrollEl.value
  if (el) {
    pendingScrollAnchor = {
      prevScrollHeight: el.scrollHeight,
      prevScrollTop: el.scrollTop
    }
  }
  emit('load-older')
}

onMounted(async () => {
  await nextTick()
  // Seed the prev-id trackers so the first incoming append isn't mistaken
  // for a prepend (which would trigger the scroll-anchor branch).
  const first = props.messages[0]
  const last = props.messages[props.messages.length - 1]
  prevFirstId = first?.id || first?.clientMsgId || null
  prevLastId = last?.id || last?.clientMsgId || null
  scrollToBottom(false)
})

watch(() => props.messages.length, async (newLen, oldLen) => {
  await nextTick()
  const last = props.messages[props.messages.length - 1]
  const lastId = last?.id || last?.clientMsgId || null
  const first = props.messages[0]
  const firstId = first?.id || first?.clientMsgId || null
  const isNewMessage = lastId && lastId !== prevLastId
  const isPrepended = firstId && prevFirstId && firstId !== prevFirstId
  prevLastId = lastId
  prevFirstId = firstId

  // Older-message load just finished: restore the scroll position so the
  // user stays anchored on the first previously-visible message instead of
  // being yanked back to the top.
  if (isPrepended && pendingScrollAnchor) {
    const el = scrollEl.value
    if (el) {
      const delta = el.scrollHeight - pendingScrollAnchor.prevScrollHeight
      el.scrollTop = pendingScrollAnchor.prevScrollTop + delta
    }
    pendingScrollAnchor = null
    return
  }

  if (!isNewMessage) return
  if (pinnedToBottom.value || last?.sendState === 'sending') {
    scrollToBottom(false)
  } else if (newLen > oldLen) {
    newMessagesBelow.value += (newLen - oldLen)
  }
})
</script>

<style scoped>
/* Jump-to highlight — pulses a soft cyan ring around the target
   message for ~1.4s after a pinned-banner click or quote-reply jump,
   then fades out. Bypasses backdrop-filter so it works on Linux where
   filter perf can be patchy. */
.msg-highlight {
  position: relative;
  animation: msg-pulse 1.4s ease-out 1;
}
@keyframes msg-pulse {
  0%   { background-color: rgba(0, 242, 255, 0.18); }
  100% { background-color: transparent; }
}
@media (prefers-reduced-motion: reduce) {
  .msg-highlight { animation: none; background-color: rgba(0, 242, 255, 0.10); }
}
</style>
